/**
 * Badge - Unified badge primitive for consistent status indicators
 *
 * Usage:
 *   <Badge intent="success">Active</Badge>
 *   <Badge intent="danger" variant="solid">Urgent</Badge>
 *   <Badge intent="warning" size="sm" pill>Drifting</Badge>
 */
import { type ReactNode } from 'react'

export type BadgeIntent = 'success' | 'warning' | 'danger' | 'info' | 'neutral'
export type BadgeVariant = 'soft' | 'solid' | 'outline'
export type BadgeSize = 'sm' | 'md'

export interface BadgeProps {
  intent?: BadgeIntent
  variant?: BadgeVariant
  size?: BadgeSize
  pill?: boolean
  className?: string
  title?: string
  children: ReactNode
}

// Color mappings using design tokens from tailwind.config.js
const INTENT_COLORS: Record<BadgeIntent, Record<BadgeVariant, string>> = {
  success: {
    soft: 'bg-success-100 text-success-700',
    solid: 'bg-success-600 text-white',
    outline: 'border border-success-300 text-success-700',
  },
  warning: {
    soft: 'bg-warning-100 text-warning-700',
    solid: 'bg-warning-500 text-white',
    outline: 'border border-warning-300 text-warning-700',
  },
  danger: {
    soft: 'bg-danger-100 text-danger-700',
    solid: 'bg-danger-600 text-white',
    outline: 'border border-danger-300 text-danger-700',
  },
  info: {
    soft: 'bg-info-100 text-info-700',
    solid: 'bg-info-600 text-white',
    outline: 'border border-info-300 text-info-700',
  },
  neutral: {
    soft: 'bg-gray-100 text-gray-700',
    solid: 'bg-gray-600 text-white',
    outline: 'border border-gray-300 text-gray-700',
  },
}

const SIZE_CLASSES: Record<BadgeSize, string> = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-0.5 text-xs',
}

export function Badge({
  intent = 'neutral',
  variant = 'soft',
  size = 'md',
  pill = false,
  className = '',
  title,
  children,
}: BadgeProps) {
  const colorClass = INTENT_COLORS[intent][variant]
  const sizeClass = SIZE_CLASSES[size]
  const roundedClass = pill ? 'rounded-full' : 'rounded'

  return (
    <span
      className={`inline-flex items-center font-medium ${roundedClass} ${sizeClass} ${colorClass} ${className}`.trim()}
      title={title}
    >
      {children}
    </span>
  )
}
